import React from 'react';
import useFetch from 'use-http';
import Paginate from 'components/UI/Paginate';
import usePaginate from 'hooks/usePaginate';
import Table, { TableFilter } from 'components/UI/Table';
import moment from 'moment-timezone';
import { useUnit } from 'effector-react';
import { $store } from 'models/store';
import { $user } from 'models/user';
import { GroupsEnum } from 'types/users';
import { createSortHandler } from '../Sessions/common/tableUtils';
import { DateFilter } from '../Sessions/common/DateFilter';
import { ExcelExportButton } from '../Sessions/common/ExcelExportButton';

const PER_PAGE = 25;

interface IOpeningStoreStat {
	id: number;
	storeNumber: string;
	storeName: string;
	fio: string;
	position: string;
	hoursFromRequest: number;
	timeInterval: string;
	hoursPayable: number;
}

// Компоненты фильтров
const DateFilterComponent = ({
	filter,
	setFilter,
}: {
	filter: TableFilter;
	setFilter: (filter: TableFilter) => void;
}) => <DateFilter filter={filter} setFilter={setFilter} />;

// Функция для создания фильтра даты
const createDateFilterFunction = (filter: TableFilter, setFilter: (filter: TableFilter) => void) => () =>
	<DateFilterComponent filter={filter} setFilter={setFilter} />;

// Функция для создания экспорта Excel
const createOpeningStoresExcelExportFunction = (filter: TableFilter, user: any, store: any) => () => {
	const newFilters = { ...filter };

	// Формируем динамическое название файла с датой
	const currentDate = moment().format('DD.MM.YYYY');
	let filename = `Статистика_по_открывающимся_магазинам_${currentDate}_за_период`;

	// Добавляем диапазон дат если они указаны
	if (filter.fromDate || filter.toDate) {
		const fromDateStr = filter.fromDate ? moment(filter.fromDate).format('DD.MM.YYYY') : '';
		const toDateStr = filter.toDate ? moment(filter.toDate).format('DD.MM.YYYY') : '';

		if (fromDateStr && toDateStr) {
			filename += `_${fromDateStr}-${toDateStr}`;
		} else if (fromDateStr) {
			filename += `_от_${fromDateStr}`;
		} else if (toDateStr) {
			filename += `_до_${toDateStr}`;
		}
	}

	filename += '.xlsx';

	return (
		<ExcelExportButton
			filter={newFilters}
			endpoint="/api/session/opening-stores-stats/reportToXls"
			filename={filename}
		/>
	);
};

export default function OpeningStoresStats() {
	const store = useUnit($store);
	const user = useUnit($user);
	const { error, loading, get } = useFetch();

	const [filter, setFilter] = React.useState<TableFilter>(
		new TableFilter({
			fromDate: new Date(new Date().setDate(1)),
			toDate: new Date(),
		}),
	);
	const { pageCount, offset, setCount, handlePageClick, currentPage } = usePaginate(PER_PAGE);
	const [data, setData] = React.useState<IOpeningStoreStat[]>([]);

	const loadData = React.useCallback(async () => {
		const params = new URLSearchParams();

		if (filter.fromDate) {
			params.append('fromDate', moment(filter.fromDate).format('YYYY-MM-DD'));
		}
		if (filter.toDate) {
			params.append('toDate', moment(filter.toDate).format('YYYY-MM-DD'));
		}
		if (filter.storeId) {
			params.append('storeId', filter.storeId.toString());
		}
		if (filter.sortKey) {
			params.append('sortKey', filter.sortKey);
		}
		if (filter.sortDirection) {
			params.append('sortDirection', filter.sortDirection);
		}

		params.append('offset', offset.toString());
		params.append('limit', PER_PAGE.toString());

		try {
			const response = await get(`/api/session/opening-stores-stats?${params.toString()}`);
			if (response?.rows) {
				setData(response.rows);
				return response;
			}
		} catch (error) {
			console.error('Ошибка при загрузке статистики:', error);
			setData([]);
		}
		return null;
	}, [filter, offset, get]);

	React.useEffect(() => {
		loadData().then(result => {
			if (result) {
				setCount(result.count);
			}
		});
	}, [offset, filter, store, loadData]);

	const headers = [
		{
			value: 'storeNumber',
			title: 'Номер магазина',
			width: '120px',
			formatted: React.useCallback((row: IOpeningStoreStat) => {
				return <div>{row.storeNumber}</div>;
			}, []),
		},
		{
			value: 'storeName',
			title: 'Название магазина',
			width: '200px',
			formatted: React.useCallback((row: IOpeningStoreStat) => {
				return <div>{row.storeName}</div>;
			}, []),
		},
		{
			value: 'fio',
			title: 'ФИО',
			width: '200px',
			formatted: React.useCallback((row: IOpeningStoreStat) => {
				return <div>{row.fio}</div>;
			}, []),
			filter: createDateFilterFunction(filter, setFilter),
		},
		{
			value: 'position',
			title: 'Тип услуги',
			width: '150px',
			formatted: React.useCallback((row: IOpeningStoreStat) => {
				return <div>{row.position}</div>;
			}, []),
		},
		{
			value: 'hoursFromRequest',
			title: 'Кол-во ч из заявки',
			width: '120px',
			formatted: React.useCallback((row: IOpeningStoreStat) => {
				return <div>{row.hoursFromRequest || '-'}</div>;
			}, []),
		},
		{
			value: 'timeInterval',
			title: 'Время смены',
			width: '150px',
			formatted: React.useCallback((row: IOpeningStoreStat) => {
				return <div>{row.timeInterval}</div>;
			}, []),
		},
		{
			value: 'hoursPayable',
			title: 'Кол-во ч к ОПЛАТЕ',
			width: '120px',
			formatted: React.useCallback((row: IOpeningStoreStat) => {
				return <div>{row.hoursPayable || '-'}</div>;
			}, []),
			filter: createOpeningStoresExcelExportFunction(filter, user, store),
		},
	];

	return (
		<div>
		{error && 'Error!'}
		<Table headers={headers} rows={data} isLoading={loading} />
		{pageCount > 1 && <Paginate handlePageClick={handlePageClick} pageCount={pageCount} forcePage={currentPage} />}
		</div>
	);
}
